import type { Action, Agent } from '@lucidtech/las-sdk-browser';
import { useCallback, useMemo, useState } from 'react';

import { TriggerOption } from '@/components/flow';
import { toActionId, toAgentId, useCreateAction, useDeleteAction, useUpdateAgent } from '@/hooks/api';

export type UseAddTriggerOnSuccess = ({ action, agent }: { action: Action; agent: Agent }) => void;

export type UseAddTriggerOnError = ({ error }: { error: Error }) => void;

export type UseAddTriggerCallbackArgs = {
  agentId: string;
  triggerOption: TriggerOption;
  onSuccess?: UseAddTriggerOnSuccess;
  onError?: UseAddTriggerOnError;
};

export type UseAddTriggerCallback = ({
  agentId,
  triggerOption,
  onSuccess,
  onError,
}: UseAddTriggerCallbackArgs) => Promise<Action>;

export type UseAddTriggerOpts = {
  onSuccess?: UseAddTriggerOnSuccess;
  onError?: UseAddTriggerOnError;
};

export const useAddTrigger = ({ onSuccess: onSuccessFnBase, onError: onErrorFnBase }: UseAddTriggerOpts = {}) => {
  const {
    mutateAsync: updateAgent,
    isPending: isPendingUpdateAgent,
    isSuccess: isSuccessUpdateAgent,
    error: errorUpdateAgent,
  } = useUpdateAgent();

  const {
    mutateAsync: createAction,
    isPending: isPendingCreateAction,
    isSuccess: isSuccessCreateAction,
    error: errorCreateAction,
  } = useCreateAction();

  const { mutateAsync: deleteAction } = useDeleteAction();

  const [error, setError] = useState<Error | null>(null);

  const addTrigger: UseAddTriggerCallback = useCallback(
    async ({ agentId, triggerOption, onSuccess: onSuccessFn, onError: onErrorFn }: UseAddTriggerCallbackArgs) => {
      setError(null);

      const onSuccess: UseAddTriggerOnSuccess = (...args) => {
        onSuccessFnBase?.(...args);
        onSuccessFn?.(...args);
      };

      const onError: UseAddTriggerOnError = (...args) => {
        onErrorFnBase?.(...args);
        onErrorFn?.(...args);
      };

      const cleanupFns: (() => Promise<unknown>)[] = [];

      const onCleanup = async () => {
        /* cleanup in reverse order */
        for (const fn of cleanupFns.toReversed()) {
          try {
            await fn();
          } catch (error) {
            console.error(error);
          }
        }
      };

      try {
        const action = await createAction({
          config: typeof triggerOption.config === 'function' ? triggerOption.config() : triggerOption.config,
          functionId: triggerOption.functionId,
          name: triggerOption.name,
        });

        cleanupFns.push(() => deleteAction(toActionId(action.actionId)));

        const agent = await updateAgent({
          id: toAgentId(agentId),
          updates: (agent: Agent) => ({
            resourceIds: [...agent.resourceIds, action.actionId],
          }),
        });

        onSuccess?.({ action, agent });

        return action;
      } catch (e) {
        console.error(e);
        const error = e instanceof Error ? e : new Error('An unknown error occurred');

        onCleanup().finally(() => {
          setError(error);
          onError?.({ error });
        });
        throw error;
      }
    },
    [createAction, deleteAction, onErrorFnBase, onSuccessFnBase, updateAgent]
  );

  const errors = useMemo(() => {
    const errors = [errorCreateAction, errorUpdateAgent, error].filter((e) => !!e);
    return errors.length > 0 ? errors : undefined;
  }, [error, errorCreateAction, errorUpdateAgent]);

  return {
    addTrigger,
    isPending: isPendingCreateAction || isPendingUpdateAgent,
    isSuccess: isSuccessCreateAction && isSuccessUpdateAgent,
    errors,
  };
};
